import React from 'react';
import { Box, Typography, Card, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { IntelligenceInsight } from '../../../types/intelligence';
import type { InsightType } from '../../../../shared/intelligence/types';
import { getIntelligenceColors, limeColors } from '../../../utils/intelligenceTheme';

interface InsightChipProps {
  insight: IntelligenceInsight;
}

const getInsightColor = (type: InsightType, theme: any, isDark: boolean) => {
  if (isDark) {
    // In dark mode, use lime for all insights (matching the example design)
    return limeColors[400];
  }

  // Light mode: use theme colors
  const colorMap = {
    positive: theme.palette.success.main,
    neutral: theme.palette.info.main,
    caution: theme.palette.warning.main,
    negative: theme.palette.error.main,
  };
  return colorMap[type] || colorMap.neutral;
};

export const InsightChip: React.FC<InsightChipProps> = React.memo(({ insight }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const intelColors = getIntelligenceColors(isDark);
  const { title, text, type } = insight;
  const insightColor = getInsightColor(type, theme, isDark);

  return (
    <Card
      sx={{
        backgroundColor: isDark ? `${intelColors!.background.secondary}80` : alpha(theme.palette.background.paper, 0.5),
        border: `1px solid ${isDark ? intelColors!.border.primary : theme.palette.divider}`,
        borderRadius: 3,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 200,
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: isDark ? `${intelColors!.background.secondary}B3` : alpha(theme.palette.background.paper, 0.8),
          transform: 'translateY(-1px)',
          boxShadow: theme.shadows[2],
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: insightColor,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: isDark ? intelColors!.text.primary : theme.palette.text.primary,
            fontSize: '0.875rem',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: isDark ? intelColors!.text.muted : theme.palette.text.secondary,
          fontSize: '0.75rem',
          lineHeight: 1.4,
        }}
      >
        {text}
      </Typography>
    </Card>
  );
});

export default InsightChip;